import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, UserIcon, WalletIcon, StarIcon, ShoppingBagIcon, PhoneIcon, CalendarIcon } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Tabs } from '../../components/ui/Tabs';
import { formatVND, formatPhone, formatDate, formatDateTime, getTierNameVi } from '../../lib/formatters';
import { MOCK_CUSTOMERS, MOCK_WALLET_TRANSACTIONS, MOCK_POINTS_TRANSACTIONS, MOCK_PURCHASES } from '../../lib/mockData';
export function CustomerDetailPage() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('wallet');
  const customer = MOCK_CUSTOMERS.find(c => c.id === id);
  if (!customer) {
    return <div className="text-center py-16" data-id="element-672">
        <p className="text-[#666666]" data-id="element-673">Không tìm thấy khách hàng</p>
        <Button variant="ghost" onClick={() => navigate('/admin/khach-hang')} className="mt-4" data-id="element-674">
          Quay lại
        </Button>
      </div>;
  }
  const walletTxs = MOCK_WALLET_TRANSACTIONS.filter(t => t.customerId === id);
  const pointsTxs = MOCK_POINTS_TRANSACTIONS.filter(t => t.customerId === id);
  const purchases = MOCK_PURCHASES.filter(p => p.customerId === id);
  const tabs = [{
    id: 'wallet',
    label: 'Ví',
    count: walletTxs.length
  }, {
    id: 'points',
    label: 'Điểm',
    count: pointsTxs.length
  }, {
    id: 'purchases',
    label: 'Mua hàng',
    count: purchases.length
  }];
  return <div data-id="element-675">
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-676">
        <button onClick={() => navigate('/admin/khach-hang')} className="flex items-center gap-1 text-sm text-[#B0B0B0] hover:text-white mb-4 transition-colors" data-id="element-677">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-678" />
          Quay lại danh sách
        </button>
      </motion.div>

      {/* Profile Header */}
      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.05
    }} data-id="element-679">
        <Card className="mb-6" data-id="element-680">
          <div className="flex flex-col md:flex-row md:items-center gap-4" data-id="element-681">
            <div className="w-16 h-16 rounded-xl bg-[#C8A84E]/10 flex items-center justify-center flex-shrink-0" data-id="element-682">
              <UserIcon className="w-8 h-8 text-[#C8A84E]" data-id="element-683" />
            </div>
            <div className="flex-1" data-id="element-684">
              <div className="flex items-center gap-3 mb-1" data-id="element-685">
                <h1 className="text-xl font-bold text-white" data-id="element-686">
                  {customer.name || 'Chưa cập nhật'}
                </h1>
                <Badge variant="tier" tier={customer.tier} size="md" data-id="element-687">
                  {getTierNameVi(customer.tier)}
                </Badge>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-[#B0B0B0]" data-id="element-688">
                <span className="flex items-center gap-1" data-id="element-689">
                  <PhoneIcon className="w-3.5 h-3.5" data-id="element-690" />
                  {formatPhone(customer.phone)}
                </span>
                {customer.dateOfBirth && <span className="flex items-center gap-1" data-id="element-691">
                    <CalendarIcon className="w-3.5 h-3.5" data-id="element-692" />
                    {formatDate(customer.dateOfBirth)}
                  </span>}
                <span className="text-[#666666]" data-id="element-693">ID: {customer.id}</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2" data-id="element-694">
                {customer.tags.map(tag => <Badge key={tag} size="sm" data-id="element-695">
                    {tag}
                  </Badge>)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-4 border-t border-[#2A2A2A]" data-id="element-696">
            <div data-id="element-697">
              <p className="text-xs text-[#666666] mb-0.5" data-id="element-698">Số dư ví</p>
              <p className="text-lg font-bold text-white" data-id="element-699">
                {formatVND(customer.walletBalance)}
              </p>
            </div>
            <div data-id="element-700">
              <p className="text-xs text-[#666666] mb-0.5" data-id="element-701">Điểm</p>
              <p className="text-lg font-bold text-white" data-id="element-702">
                {customer.points.toLocaleString('vi-VN')}
              </p>
            </div>
            <div data-id="element-703">
              <p className="text-xs text-[#666666] mb-0.5" data-id="element-704">Tổng nạp</p>
              <p className="text-lg font-bold text-[#22C55E]" data-id="element-705">
                {formatVND(customer.totalDeposited)}
              </p>
            </div>
            <div data-id="element-706">
              <p className="text-xs text-[#666666] mb-0.5" data-id="element-707">Tổng chi tiêu</p>
              <p className="text-lg font-bold text-white" data-id="element-708">
                {formatVND(customer.totalSpent)}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} className="mb-4" data-id="element-709" />

      <Card padding="none" data-id="element-710">
        {activeTab === 'wallet' && <div className="divide-y divide-[#2A2A2A]/50" data-id="element-711">
            {walletTxs.length === 0 ? <p className="text-center py-8 text-[#666666]" data-id="element-712">
                Chưa có giao dịch ví
              </p> : walletTxs.map(tx => <div key={tx.id} className="flex items-center gap-3 px-5 py-3" data-id="element-713">
                  <div className="flex-1 min-w-0" data-id="element-714">
                    <p className="text-sm text-white truncate" data-id="element-715">
                      {tx.description}
                    </p>
                    <p className="text-xs text-[#666666]" data-id="element-716">
                      {formatDateTime(tx.createdAt)}
                    </p>
                  </div>
                  <Badge variant={tx.type === 'purchase' ? 'error' : tx.type === 'bonus' ? 'gold' : 'success'} size="sm" data-id="element-717">
                    {tx.type}
                  </Badge>
                  <span className={`text-sm font-semibold ${tx.amount >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-718">
                    {tx.amount >= 0 ? '+' : ''}
                    {formatVND(tx.amount)}
                  </span>
                </div>)}
          </div>}

        {activeTab === 'points' && <div className="divide-y divide-[#2A2A2A]/50" data-id="element-719">
            {pointsTxs.length === 0 ? <p className="text-center py-8 text-[#666666]" data-id="element-720">
                Chưa có hoạt động điểm
              </p> : pointsTxs.map(tx => <div key={tx.id} className="flex items-center gap-3 px-5 py-3" data-id="element-721">
                  <div className="flex-1 min-w-0" data-id="element-722">
                    <p className="text-sm text-white truncate" data-id="element-723">
                      {tx.description}
                    </p>
                    <p className="text-xs text-[#666666]" data-id="element-724">
                      {formatDateTime(tx.createdAt)}
                    </p>
                  </div>
                  <span className={`text-sm font-semibold ${tx.points >= 0 ? 'text-[#22C55E]' : 'text-[#EF4444]'}`} data-id="element-725">
                    {tx.points >= 0 ? '+' : ''}
                    {tx.points} điểm
                  </span>
                </div>)}
          </div>}

        {activeTab === 'purchases' && <div className="divide-y divide-[#2A2A2A]/50" data-id="element-726">
            {purchases.length === 0 ? <p className="text-center py-8 text-[#666666]" data-id="element-727">
                Chưa có lịch sử mua hàng
              </p> : purchases.map(p => <div key={p.id} className="flex items-center gap-3 px-5 py-3" data-id="element-728">
                  <ShoppingBagIcon className="w-4 h-4 text-[#C8A84E] flex-shrink-0" data-id="element-729" />
                  <div className="flex-1 min-w-0" data-id="element-730">
                    <p className="text-sm text-white" data-id="element-731">{formatVND(p.amount)}</p>
                    <p className="text-xs text-[#666666]" data-id="element-732">
                      {formatDateTime(p.createdAt)} · Giảm{' '}
                      {formatVND(p.discountApplied)} · +{p.pointsEarned} điểm
                    </p>
                  </div>
                  <span className="text-sm text-[#B0B0B0]" data-id="element-733">
                    {formatVND(p.creditsUsed)} credits
                  </span>
                </div>)}
          </div>}
      </Card>
    </div>;
}